
import { apiCall } from '../utils/apiUtils';
import { AdMetrics } from '../types/ads';

// Enhanced API call with CORS error handling
const apiCallWithCORSHandling = async (url: string, options: any = {}): Promise<any> => {
  try {
    const response = await apiCall(url, options);
    return response;
  } catch (error: any) {
    console.warn('API call failed, possibly due to CORS:', error);
    
    // If it's a CORS error, return a fallback response
    if (error.message?.includes('CORS') || 
        error.message?.includes('blocked') ||
        error.message?.includes('preflight') ||
        error.name === 'TypeError') {
      
      console.log('🔄 Returning fallback data for CORS error');
      
      // Return appropriate fallback based on the URL
      if (url.includes('targeting')) {
        return {
          demographics: { age: '25-34', gender: 'all', location: 'US' },
          interests: ['fashion', 'style'],
          behavioral: { fashionEngagement: 'high' },
          generated: true,
          fallback: true
        };
      }
      
      if (url.includes('analytics')) {
        return { success: true, fallback: true };
      }
      
      // Default fallback
      return { success: false, error: 'CORS_ERROR', fallback: true };
    }
    
    throw error;
  }
};

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

export interface AnalyticsData {
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  ctr: number;
  cpm: number;
  timestamp: string;
}

export interface AnalyticsMetric {
  id: string;
  name: string;
  value: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
}

export interface AnalyticsResponse {
  success: boolean;
  data: AnalyticsData[];
  metrics: AnalyticsMetric[];
  totalRevenue: number;
  totalImpressions: number;
}

export class AdAnalyticsService {
  private static instance: AdAnalyticsService;
  
  public static getInstance(): AdAnalyticsService {
    if (!AdAnalyticsService.instance) {
      AdAnalyticsService.instance = new AdAnalyticsService();
    }
    return AdAnalyticsService.instance;
  }

  async getAnalyticsData(dateRange?: { start: string; end: string }): Promise<AnalyticsResponse> {
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('start', dateRange.start);
        params.append('end', dateRange.end);
      }

      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/ads?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      return {
        success: false,
        data: [],
        metrics: [],
        totalRevenue: 0,
        totalImpressions: 0
      };
    }
  }

  async trackAdImpression(slotId: string, impressionData: any): Promise<boolean> {
    try {
      await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/track/impression`, {
        method: 'POST',
        data: { 
          slotId, 
          ...impressionData,
          timestamp: new Date().toISOString() 
        }
      });
      return true;
    } catch (error) {
      console.error('Error tracking ad impression:', error);
      return false;
    }
  }

  async trackAdClick(slotId: string, clickData: any): Promise<boolean> {
    try {
      await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/track/click`, {
        method: 'POST',
        data: { 
          slotId, 
          ...clickData,
          timestamp: new Date().toISOString() 
        }
      });
      return true;
    } catch (error) {
      console.error('Error tracking ad click:', error);
      return false;
    }
  }

  async trackConversion(adId: string, conversionValue: number): Promise<boolean> {
    try {
      await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/conversion`, {
        method: 'POST',
        data: { 
          adId, 
          conversionValue, 
          timestamp: new Date().toISOString() 
        }
      });
      return true;
    } catch (error) {
      console.error('Error tracking conversion:', error);
      return false;
    }
  }

  async getRevenueMetrics(): Promise<AnalyticsMetric[]> {
    try {
      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/revenue-metrics`);
      return response.metrics || [];
    } catch (error) {
      console.error('Error fetching revenue metrics:', error);
      return [];
    }
  }

  async getPerformanceReport(adId?: string): Promise<AnalyticsData[]> {
    try {
      const params = new URLSearchParams();
      if (adId) params.append('adId', adId);

      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/performance?${params.toString()}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching performance report:', error);
      return [];
    }
  }

  async generateReport(options: { days: number }): Promise<any> {
    try {
      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/report?days=${options.days}`);
      return response;
    } catch (error) {
      console.error('Error generating analytics report:', error);
      return {
        success: false,
        data: [],
        summary: {
          totalRevenue: 0,
          totalImpressions: 0,
          totalClicks: 0,
          averageCTR: 0,
          averageCPM: 0
        }
      };
    }
  }

  async trackAdInteraction(slotId: string, interactionData: any): Promise<boolean> {
    try {
      await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/interaction`, {
        method: 'POST',
        data: {
          slotId,
          ...interactionData,
          timestamp: new Date().toISOString()
        }
      });
      return true;
    } catch (error) {
      console.error('Error tracking ad interaction:', error);
      return false;
    }
  }

  async trackError(errorData: any): Promise<boolean> {
    try {
      await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/error`, {
        method: 'POST',
        data: {
          ...errorData,
          timestamp: new Date().toISOString()
        }
      });
      return true;
    } catch (error) {
      console.error('Error tracking analytics error:', error);
      return false;
    }
  }

  async getRealTimeMetrics(): Promise<AdMetrics> {
    try {
      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/analytics/real-time-metrics`);
      return {
        rpm: response.rpm || 0,
        fillRate: response.fillRate || 0,
        ctr: response.ctr || 0,
        viewability: response.viewability || 0,
        loadTime: response.loadTime || 0,
        revenue: response.revenue || 0
      };
    } catch (error) {
      console.error('Error fetching real-time metrics:', error);
      return {
        rpm: 0,
        fillRate: 0,
        ctr: 0,
        viewability: 0,
        loadTime: 0,
        revenue: 0
      };
    }
  }
}

export const adAnalyticsService = AdAnalyticsService.getInstance();
export const adAnalytics = adAnalyticsService;
